/**
 * useOptimalTeamsWorker Hook
 *
 * Offloads optimal team calculations to a Web Worker to avoid blocking the UI.
 * Provides progress updates and cancellation support.
 *
 * Both the worker and the synchronous fallback run the SAME shared algorithm
 * from lib/algorithms/optimal-teams-core.ts — do not duplicate it here.
 */

import { useEffect, useRef, useState } from "react";
import type {
  ChipType,
  OptimalTeam,
  PredictedConstructor,
  PredictedDriver,
  WorkerInput,
  WorkerOutput,
} from "../lib/algorithms";
import { computeOptimalTeams } from "../lib/algorithms/optimal-teams-core";
import type { TeamFilters } from "../lib/team-filters";

export interface UseOptimalTeamsWorkerOptions {
  drivers: PredictedDriver[];
  constructors: PredictedConstructor[];
  budget: number;
  maxTeams?: number;
  requiredDriverSlugs?: string[];
  requiredConstructorSlugs?: string[];
  activeChip?: ChipType;
  algorithmId: string;
  algorithmName: string;
  enabled?: boolean;
  requiredX2DriverSlug?: string | null;
  requiredX3DriverSlug?: string | null;
  excludedDriverSlugs?: string[];
  excludedConstructorSlugs?: string[];
  teamFilters?: TeamFilters | null;
}

export interface UseOptimalTeamsWorkerResult {
  teams: OptimalTeam[];
  isCalculating: boolean;
  progress: number;
  totalTeamsFound: number; // Total valid combinations (before limiting to maxTeams)
  totalCombinationsEvaluated: number; // All combinations checked
  error: string | null;
}

/**
 * Hook that calculates optimal teams using a Web Worker.
 *
 * The calculation runs in a background thread, reporting progress
 * back to the main thread without blocking the UI.
 */
export function useOptimalTeamsWorker(
  options: UseOptimalTeamsWorkerOptions,
): UseOptimalTeamsWorkerResult {
  const {
    drivers,
    constructors,
    budget,
    maxTeams = 20,
    requiredDriverSlugs = [],
    requiredConstructorSlugs = [],
    activeChip = null,
    algorithmId,
    algorithmName,
    enabled = true,
    requiredX2DriverSlug = null,
    requiredX3DriverSlug = null,
    excludedDriverSlugs = [],
    excludedConstructorSlugs = [],
    teamFilters = null,
  } = options;

  const [teams, setTeams] = useState<OptimalTeam[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalTeamsFound, setTotalTeamsFound] = useState(0);
  const [totalCombinationsEvaluated, setTotalCombinationsEvaluated] =
    useState(0);
  const [error, setError] = useState<string | null>(null);

  const workerRef = useRef<Worker | null>(null);

  // Clean up worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  // Run calculation when inputs change
  useEffect(() => {
    if (!enabled || drivers.length === 0 || constructors.length === 0) {
      setTeams([]);
      setIsCalculating(false);
      setProgress(0);
      setTotalTeamsFound(0);
      setTotalCombinationsEvaluated(0);
      setError(null);
      return;
    }

    // Terminate previous worker if running
    if (workerRef.current) {
      workerRef.current.terminate();
    }

    // Reset state
    setIsCalculating(true);
    setProgress(0);
    setError(null);

    // Create new worker
    // Note: Vite supports ?worker import syntax for web workers
    const worker = new Worker(
      new URL(
        "../lib/algorithms/worker/optimal-teams.worker.ts",
        import.meta.url,
      ),
      { type: "module" },
    );
    workerRef.current = worker;

    // Handle messages from worker
    worker.onmessage = (e: MessageEvent<WorkerOutput>) => {
      const message = e.data;

      switch (message.type) {
        case "progress":
          setProgress(message.progress ?? 0);
          break;

        case "complete":
          setTeams(message.teams ?? []);
          setIsCalculating(false);
          setProgress(100);
          setTotalTeamsFound(message.totalTeamsFound ?? 0);
          setTotalCombinationsEvaluated(
            message.totalCombinationsEvaluated ?? 0,
          );
          break;

        case "error":
          setError(message.error ?? "Unknown error");
          setIsCalculating(false);
          setTeams([]);
          break;
      }
    };

    worker.onerror = (e) => {
      console.error("Worker error:", e);
      setError(e.message || "Worker error");
      setIsCalculating(false);
    };

    // Send input to worker
    const input: WorkerInput = {
      drivers,
      constructors,
      budget,
      maxTeams,
      requiredDriverSlugs,
      requiredConstructorSlugs,
      activeChip,
      algorithmId,
      algorithmName,
      requiredX2DriverSlug,
      requiredX3DriverSlug,
      excludedDriverSlugs,
      excludedConstructorSlugs,
      teamFilters,
    };

    worker.postMessage(input);

    // Cleanup function
    return () => {
      worker.terminate();
      if (workerRef.current === worker) {
        workerRef.current = null;
      }
    };
  }, [
    enabled,
    drivers,
    constructors,
    budget,
    maxTeams,
    // Serialize arrays to stable strings for dependency comparison
    requiredDriverSlugs.join(","),
    requiredConstructorSlugs.join(","),
    activeChip,
    algorithmId,
    algorithmName,
    requiredX2DriverSlug,
    requiredX3DriverSlug,
    excludedDriverSlugs.join(","),
    excludedConstructorSlugs.join(","),
    JSON.stringify(teamFilters),
  ]);

  return {
    teams,
    isCalculating,
    progress,
    totalTeamsFound,
    totalCombinationsEvaluated,
    error,
  };
}

/**
 * Fallback synchronous calculation for environments without Worker support.
 * Runs the exact same shared algorithm as the worker, on the main thread.
 */
export function useOptimalTeamsSync(
  options: UseOptimalTeamsWorkerOptions,
): UseOptimalTeamsWorkerResult {
  const {
    drivers,
    constructors,
    budget,
    maxTeams = 20,
    requiredDriverSlugs = [],
    requiredConstructorSlugs = [],
    activeChip = null,
    algorithmId,
    algorithmName,
    enabled = true,
    requiredX2DriverSlug = null,
    requiredX3DriverSlug = null,
    excludedDriverSlugs = [],
    excludedConstructorSlugs = [],
    teamFilters = null,
  } = options;

  const [teams, setTeams] = useState<OptimalTeam[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [totalTeamsFound, setTotalTeamsFound] = useState(0);
  const [totalCombinationsEvaluated, setTotalCombinationsEvaluated] =
    useState(0);

  useEffect(() => {
    if (!enabled || drivers.length === 0 || constructors.length === 0) {
      setTeams([]);
      setTotalTeamsFound(0);
      setTotalCombinationsEvaluated(0);
      return;
    }

    setIsCalculating(true);

    // Use setTimeout to not block initial render
    const timeoutId = setTimeout(() => {
      const result = computeOptimalTeams({
        drivers,
        constructors,
        budget,
        maxTeams,
        requiredDriverSlugs,
        requiredConstructorSlugs,
        activeChip,
        algorithmId,
        algorithmName,
        requiredX2DriverSlug,
        requiredX3DriverSlug,
        excludedDriverSlugs,
        excludedConstructorSlugs,
        teamFilters,
      });
      setTeams(result.teams);
      setTotalTeamsFound(result.totalTeamsFound);
      setTotalCombinationsEvaluated(result.totalCombinationsEvaluated);
      setIsCalculating(false);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [
    enabled,
    drivers,
    constructors,
    budget,
    maxTeams,
    requiredDriverSlugs.join(","),
    requiredConstructorSlugs.join(","),
    activeChip,
    algorithmId,
    algorithmName,
    requiredX2DriverSlug,
    requiredX3DriverSlug,
    excludedDriverSlugs.join(","),
    excludedConstructorSlugs.join(","),
    JSON.stringify(teamFilters),
  ]);

  return {
    teams,
    isCalculating,
    progress: isCalculating ? 50 : 100,
    totalTeamsFound,
    totalCombinationsEvaluated,
    error: null,
  };
}
